import React from 'react';
import { Link } from 'react-router';
import SEO from '../../components/SEO';
import GlowCard from '../../components/GlowCard';
import Logo from '../../components/Logo';
import FAQSection from '../../components/FAQSection';
import { trackEvent } from '../../components/analytics';

const UTMPage: React.FC = () => {
  const utmFAQ = [
    {
      question: 'Can authorities manage USSP approvals end-to-end in one workflow?',
      answer:
        'Yes. The UTM module supports certification lifecycle stages, document controls, review checkpoints, and formal approval evidence for regulator-led governance.',
    },
    {
      question: 'Does the platform support digital SORA oversight?',
      answer:
        'Yes. Authorities can structure SORA submissions, trace assumptions, track mitigation acceptance, and retain auditable records for each authorization decision.',
    },
    {
      question: 'How does the platform connect with existing AviaGov modules?',
      answer:
        'UTM links with SMS, LMS, eAIP, and PANS-OPS workflows to maintain one governance chain from occurrence oversight to training, publication, and procedural assurance.',
    },
  ];

  const capabilityCards = [
    {
      title: 'Provider Certification Lifecycle',
      points: [
        'USSP onboarding, eligibility, and gate reviews',
        'Controlled evidence submissions and validations',
        'Approval milestones with authority sign-off logs',
        'Renewal and suspension governance states',
      ],
    },
    {
      title: 'Digital SORA Workflow',
      points: [
        'Structured SORA package intake and triage',
        'Risk and mitigation review checkpoints',
        'Decision traceability across evaluator teams',
        'Versioned authorization records for audits',
      ],
    },
    {
      title: 'Remote ID Compliance Oversight',
      points: [
        'Program-level conformance monitoring views',
        'Exception capture and escalation controls',
        'Supervisory dashboards for enforcement teams',
        'Evidence trails for inspection reporting',
      ],
    },
    {
      title: 'U-Space Volume & Airspace Governance',
      points: [
        'Digital control of designated U-Space volumes',
        'Rule layers linked to authority policy',
        'Operational constraints with approval history',
        'Cross-entity visibility for governance boards',
      ],
    },
    {
      title: 'Occurrence + SMS Integration',
      points: [
        'UTM occurrences linked to safety workflows',
        'Shared investigation and mitigation references',
        'Closed-loop visibility for authority assurance',
        'Consistent evidence across oversight domains',
      ],
    },
    {
      title: 'Audit & CAPA Management',
      points: [
        'Authority audit planning and execution records',
        'Corrective actions with due-date governance',
        'CAPA ownership, status, and verification trails',
        'Inspection-ready oversight reporting packs',
      ],
    },
  ];

  return (
    <div className="animate-fade-in-up">
      <SEO
        title="UTM and U-Space Governance Software for Authorities"
        description="UTM and U-Space governance software for civil aviation authorities managing USSP certification, SORA oversight, Remote ID compliance, audit trails, and regulatory control."
        ogType="product"
        faq={utmFAQ}
      />

      <section className="py-24 bg-slate-950 border-b border-white/5 relative overflow-hidden">
        <div className="hero-glow absolute inset-0 -z-10"></div>
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <nav className="flex items-center space-x-2 text-slate-500 text-sm mb-6 uppercase tracking-widest">
              <span>Modules</span>
              <span>/</span>
              <span className="text-accent">UTM</span>
            </nav>
            <h1 className="text-5xl lg:text-7xl font-bold font-heading mb-8 leading-tight">
              UTM and U-Space governance software
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-white to-brandDeep">
                for national authorities
              </span>
            </h1>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed">
              Digitize USSP certification, SORA assessment, Remote ID compliance, auditability, and regulatory oversight for scalable unmanned aviation ecosystems.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="px-8 py-4 bg-accent text-primary font-bold rounded-xl hover:shadow-[0_0_40px_rgba(6,182,212,0.45)] transition-all"
                onClick={() => trackEvent('demo_request', { form_id: 'utm_demo', module: 'utm' })}
              >
                Request UTM Demo
              </Link>
              <a
                href="#"
                className="px-8 py-4 bg-white/5 border border-white/10 text-white font-bold rounded-xl hover:bg-white/10 transition-all"
                onClick={(event) => {
                  event.preventDefault();
                  trackEvent('generate_lead', { form_id: 'utm_executive_brief', module: 'utm' });
                }}
              >
                Download Executive Brief
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-accent/10 border border-accent/20 p-8 rounded-2xl text-center">
              <div className="text-4xl font-bold text-accent mb-2">Authority</div>
              <p className="text-xs text-slate-400 uppercase">Governance Grade</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-8 rounded-2xl text-center">
              <div className="text-4xl font-bold text-white mb-2">SORA</div>
              <p className="text-xs text-slate-400 uppercase">Digital Oversight</p>
            </div>
            <GlowCard className="col-span-2 p-8">
              <div className="text-xs text-accent font-bold uppercase tracking-widest mb-3">UTM - U-Space Governance</div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Built for civil aviation authorities requiring sovereign control, standardized evidence, and institutional accountability across UTM service governance.
              </p>
            </GlowCard>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-4 text-white">UTM Governance Capabilities</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilityCards.map((card) => (
              <GlowCard key={card.title} className="p-8">
                <div className="w-14 h-14 bg-accent/10 border border-accent/20 rounded-2xl mb-6"></div>
                <h3 className="text-white font-bold text-2xl mb-4">{card.title}</h3>
                <ul className="text-slate-400 text-sm space-y-2">
                  {card.points.map((point) => (
                    <li key={point}>• {point}</li>
                  ))}
                </ul>
              </GlowCard>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 bg-slate-950/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-6xl font-bold font-heading mb-6 text-white">Global Regulatory Alignment</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[
              'ICAO Annex 19 + UTM Framework',
              'EU U-Space Regulation 2021/664',
              'FAA Remote ID (Part 89)',
              'EUROCONTROL / SESAR U-Space ConOps',
            ].map((item) => (
              <div key={item} className="flex items-center justify-between bg-surface border border-white/10 rounded-2xl px-6 py-5">
                <span className="text-white font-semibold">{item}</span>
                <span className="text-accent font-bold">Aligned</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 border-y border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl lg:text-6xl font-bold font-heading mb-6 text-white">Integration with AviaGov</h2>
            <p className="text-slate-300 text-lg leading-relaxed mb-6">
              UTM governance connects directly to the AviaGov operating model, so authority teams keep certification oversight, safety evidence, procedure control, and publication governance in one traceable environment.
            </p>
            <ul className="text-slate-400 text-sm space-y-3">
              <li>SMS: occurrence governance and safety assurance feedback loops</li>
              <li>LMS: competency and authorization training evidence linkage</li>
              <li>eAIP: regulated publication continuity for aeronautical information</li>
              <li>PANS-OPS: procedure governance alignment for institutional control</li>
            </ul>
          </div>
          <div className="bg-surface border border-white/10 rounded-[2rem] p-10">
            <div className="mb-6 opacity-80">
              <Logo size="sm" />
            </div>
            <div className="text-sm text-slate-400 uppercase tracking-[0.3em] mb-6">Core Platform</div>
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-primary/40 border border-white/10 rounded-xl px-6 py-4">
                <span className="text-white font-bold">Connected Core</span>
                <span className="text-xs text-accent font-bold uppercase tracking-widest">Always On</span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-primary/40 border border-white/10 rounded-xl px-4 py-3 text-slate-300 text-sm">SMS</div>
                <div className="bg-primary/40 border border-white/10 rounded-xl px-4 py-3 text-slate-300 text-sm">LMS</div>
                <div className="bg-primary/40 border border-white/10 rounded-xl px-4 py-3 text-slate-300 text-sm">PANS-OPS</div>
                <div className="bg-primary/40 border border-white/10 rounded-xl px-4 py-3 text-slate-300 text-sm">eAIP</div>
                <div className="bg-primary/40 border border-accent/30 rounded-xl px-4 py-3 text-accent text-sm font-semibold">UTM</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQSection items={utmFAQ} title="UTM Governance FAQ" />
    </div>
  );
};

export default UTMPage;
